import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import Button from "../../atoms/Button/Button";
import Form from "../../atoms/Form/Form";
import { capitalize } from "../../Functions/Capitalize";
import { getIconPath } from "../../Functions/GetIconPath";
import { getAccessToken, saveToLocalStorage } from "../../Functions/LocalStorageUtils";
import { 
    useDeleteConfigurationMutation, 
    useEditConfigurationTitleMutation,
    useGetAllConfigurationsQuery, 
    useOverwriteConfigurationMutation,
    useSaveConfigurationMutation 
} from "../../redux/api/configurations";
import { resetConfiguration, resetUnsavedChangesTracking, setActuators, setAppliances, setDetectors, setFloorplanImage, setShowSaveDetail } from "../../redux/reducers/appReducer";
import { addDevice } from "../../redux/reducers/controllersReducer";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { ApiConfigurationItemI, ConfigurationI } from "../../redux/types/configurations";

import styles from "./SaveComponent.module.scss";

type SaveSortKey = 'name' | 'technology' | 'controllers' | 'appliances' | 'actuators' | 'time';
type SortDirection = 'asc' | 'desc';

const SaveComponent: React.FC = () => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const [newSaveName, setNewSaveName] = useState('');
    const [editingConfigurationId, setEditingConfigurationId] = useState<number | null>(null);
    const [editingName, setEditingName] = useState('');
    const [saveSort, setSaveSort] = useState<{ key: SaveSortKey; direction: SortDirection }>({
        key: 'time',
        direction: 'desc'
    });
    const { data: configurationsResponse, refetch } = useGetAllConfigurationsQuery(null);
    const [saveConfiguration] = useSaveConfigurationMutation();
    const [deleteConfiguration] = useDeleteConfigurationMutation();
    const [overwriteConfiguration] = useOverwriteConfigurationMutation();
    const [editConfigurationTitle, { isLoading: isEditingConfigurationTitle }] = useEditConfigurationTitleMutation();
    const controllers = useAppSelector((state) => state.controllers.devices);
    const appliances = useAppSelector((state) => state.app.appliances);
    const actuators = useAppSelector((state) => state.app.actuators);
    const detectors = useAppSelector((state) => state.app.detectors);
    const floorplanImage = useAppSelector((state) => state.app.floorplanImage);
    const tech = useAppSelector((state) => state.app.controllerType);

    const parsedConfigurations = useMemo(() => {
        if (!configurationsResponse?.data) return [];
        try {
            const items = JSON.parse(configurationsResponse.data as string) as ApiConfigurationItemI[];
            return items.map((item) => {
                let config: ConfigurationI = { controllers: [], appliances: [], actuators: [], detectors: [], floorplanImage: null };
                try { config = JSON.parse(item.data) as ConfigurationI; } catch { /* keep defaults */ }
                if (!config.actuators) {
                    config.actuators = [];
                }
                if (!config.detectors) {
                    config.detectors = [];
                }
                if (typeof config.floorplanImage === 'undefined') {
                    config.floorplanImage = null;
                }
                return { ...item, config };
            });
        } catch {
            return [];
        }
    }, [configurationsResponse]);

    const sortedConfigurations = useMemo(() => {
        const sortedItems = [...parsedConfigurations];
        const directionMultiplier = saveSort.direction === 'asc' ? 1 : -1;

        sortedItems.sort((left, right) => {
            switch (saveSort.key) {
                case 'name':
                    return left.name.toLowerCase().localeCompare(right.name.toLowerCase()) * directionMultiplier;
                case 'technology':
                    return left.tech.toLowerCase().localeCompare(right.tech.toLowerCase()) * directionMultiplier;
                case 'controllers':
                    return (left.config.controllers.length - right.config.controllers.length) * directionMultiplier;
                case 'appliances':
                    return (left.config.appliances.length - right.config.appliances.length) * directionMultiplier;
                case 'actuators':
                    return (left.config.actuators.length - right.config.actuators.length) * directionMultiplier;
                case 'time':
                    return (new Date(left.timestamp).getTime() - new Date(right.timestamp).getTime()) * directionMultiplier;
                default:
                    return 0;
            }
        });

        return sortedItems;
    }, [parsedConfigurations, saveSort]);

    const handleSaveSort = (key: SaveSortKey) => {
        setSaveSort((previous) => {
            if (previous.key === key) {
                return { key, direction: previous.direction === 'asc' ? 'desc' : 'asc' };
            }

            return { key, direction: 'asc' };
        });
    };

    const getSaveSortIndicator = (key: SaveSortKey) => {
        if (saveSort.key !== key) {
            return '';
        }

        return saveSort.direction === 'asc' ? ' ^' : ' v';
    };

    useEffect(() => {
        if(!getAccessToken()){
            dispatch(setShowSaveDetail(false));
        }
    }, [dispatch]);

    useEffect(() => {
        if(configurationsResponse?.success){
            toast.success(t('saveDetail.loadSuccess'));
        }
    }, [configurationsResponse, t]);

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        toast.info(t('saveDetail.saving'));
        try {
            await saveConfiguration({
                name: newSaveName,
                tech: tech,
                configuration: {
                    controllers: controllers,
                    appliances: appliances,
                    actuators: actuators,
                    detectors: detectors,
                    floorplanImage
                }
            }).unwrap();
            dispatch(resetUnsavedChangesTracking());
            setNewSaveName('');
            toast.success(t('saveDetail.saveSuccess'));
            await refetch();
        } catch (error) {
            toast.error(t('saveDetail.saveError'));
        }
    };
    const checkSubmit = () => {
        if(newSaveName === '')return true;
        return false;
    }

    const loadConfiguration = (configurationId: number) => {
        const configToLoad = parsedConfigurations.find((c) => c.id === configurationId);
        if(!configToLoad) {
            toast.error(t('saveDetail.loadError'));
            return;
        }
        dispatch(resetConfiguration());
        for(const controller of configToLoad.config.controllers){
            dispatch(addDevice(controller));
        }
        dispatch(setAppliances(configToLoad.config.appliances));
        dispatch(setActuators(configToLoad.config.actuators ?? []));
        dispatch(setDetectors(configToLoad.config.detectors ?? []));
        dispatch(setFloorplanImage(configToLoad.config.floorplanImage ?? null));
        saveToLocalStorage('custom_floorplan_image', configToLoad.config.floorplanImage ?? '');
        toast.success(t('saveDetail.loadSuccess'));
        dispatch(resetUnsavedChangesTracking());
    }

    const deleteConfigurationHandler = async (configurationId: number) => {
        try {
            await deleteConfiguration(configurationId);
            toast.success(t('saveDetail.deleteSuccess'));
            await refetch();
        } catch (error) {
            toast.error(t('saveDetail.deleteError') || 'Failed to delete configuration');
        }
    }

    const overwriteConfigurationHandler = async (configurationId: number, technology: string) => {
        if(technology !== tech){
            toast.error(t('saveDetail.techMissmatchError'))
            return;
        }
        try {
            await overwriteConfiguration({ id: configurationId, configuration: { controllers, appliances, actuators, detectors, floorplanImage } });
            toast.success(t('saveDetail.overwriteSuccess'));
            dispatch(resetUnsavedChangesTracking());
            await refetch();
        } catch (error) {
            toast.error(t('saveDetail.overwriteError') || 'Failed to overwrite configuration');
        }
    }

    const startRenameConfiguration = (configurationId: number, currentName: string) => {
        setEditingConfigurationId(configurationId);
        setEditingName(currentName);
    }

    const cancelRenameConfiguration = () => {
        setEditingConfigurationId(null);
        setEditingName('');
    }

    const renameConfigurationHandler = async (configurationId: number) => {
        const trimmedName = editingName.trim();
        if (!trimmedName) {
            toast.error(t('saveDetail.renameEmptyError'));
            return;
        }

        try {
            await editConfigurationTitle({ id: configurationId, name: trimmedName }).unwrap();
            toast.success(t('saveDetail.renameSuccess'));
            cancelRenameConfiguration();
            await refetch();
        } catch (error) {
            toast.error(t('saveDetail.renameError') || 'Failed to rename configuration');
        }
    }

    return (
        <>
            <div className={styles.SaveComponent}>
                <div className={styles.SaveComponent__wrapper}>
                    <img src={getIconPath('arrow_back')} alt="Back" onClick={() => dispatch(setShowSaveDetail(false))} className={styles.SaveComponent__back} />
                    <h2>{t('saveDetail.title')}</h2>
                    <p>{t('saveDetail.description')}</p>
                    <div className={styles.SaveComponent__wrapper__header}>
                        <button type="button" onClick={() => handleSaveSort('name')}>
                            {t('saveDetail.header.name')}{getSaveSortIndicator('name')}
                        </button>
                        <button type="button" onClick={() => handleSaveSort('technology')}>
                            {t('saveDetail.header.technology')}{getSaveSortIndicator('technology')}
                        </button>
                        <button type="button" onClick={() => handleSaveSort('controllers')}>
                            {t('saveDetail.header.controllers')}{getSaveSortIndicator('controllers')}
                        </button>
                        <button type="button" onClick={() => handleSaveSort('appliances')}>
                            {t('saveDetail.header.appliances')}{getSaveSortIndicator('appliances')}
                        </button>
                        <button type="button" onClick={() => handleSaveSort('actuators')}>
                            {t('saveDetail.header.actuators')}{getSaveSortIndicator('actuators')}
                        </button>
                        <button type="button" onClick={() => handleSaveSort('time')}>
                            {t('saveDetail.header.time')}{getSaveSortIndicator('time')}
                        </button>
                        <p>{t('saveDetail.header.load')}</p>
                        <p>{t('saveDetail.header.overwrite')}</p>
                        <p>{t('saveDetail.header.delete')}</p>
                    </div>
                    <div className={styles.SaveComponent__wrapper__items}>
                        {sortedConfigurations.map((item) => (
                            <div key={item.id} className={styles.SaveComponent__wrapper__item}>
                                <div className={styles.SaveComponent__wrapper__item__nameCell}>
                                    {editingConfigurationId === item.id ? (
                                        <>
                                            <input
                                                type="text"
                                                className={styles.SaveComponent__wrapper__item__nameInput}
                                                value={editingName}
                                                onChange={(event) => setEditingName(event.target.value)}
                                                onKeyDown={(event) => {
                                                    if (event.key === 'Enter') {
                                                        void renameConfigurationHandler(item.id);
                                                    }
                                                    if (event.key === 'Escape') {
                                                        cancelRenameConfiguration();
                                                    }
                                                }}
                                                disabled={isEditingConfigurationTitle}
                                                autoFocus
                                            />
                                            <div className={styles.SaveComponent__wrapper__item__nameActions}>
                                                <button
                                                    type="button"
                                                    onClick={() => renameConfigurationHandler(item.id)}
                                                    disabled={isEditingConfigurationTitle}
                                                >
                                                    {t('saveDetail.renameSave')}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={cancelRenameConfiguration}
                                                    disabled={isEditingConfigurationTitle}
                                                >
                                                    {t('saveDetail.renameCancel')}
                                                </button>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <p>{item.name}</p>
                                            <div className={styles.SaveComponent__wrapper__item__nameActions}>
                                                <button
                                                    type="button"
                                                    onClick={() => startRenameConfiguration(item.id, item.name)}
                                                >
                                                    {t('saveDetail.renameEdit')}
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </div>
                                <p>{capitalize(item.tech)}</p>
                                <p>{item.config.controllers.length}</p>
                                <p>{item.config.appliances.length}</p>
                                <p>{item.config.actuators.length}</p>
                                <p>{new Date(item.timestamp).toLocaleString()}</p>
                                <div className={styles.SaveComponent__wrapper__item__action}>
                                    <img
                                        src={getIconPath('load')}
                                        alt={t('saveDetail.header.load')}
                                        title={t('saveDetail.header.load')}
                                        onClick={() => loadConfiguration(item.id)}
                                    />
                                </div>
                                <div className={styles.SaveComponent__wrapper__item__action}>
                                    <img
                                        src={getIconPath('save')}
                                        alt={t('saveDetail.header.overwrite')}
                                        title={t('saveDetail.header.overwrite')}
                                        onClick={() => overwriteConfigurationHandler(item.id, item.tech)}
                                    />
                                </div>
                                <div className={styles.SaveComponent__wrapper__item__action}>
                                    <img
                                        src={getIconPath('ico_trash')}
                                        alt={t('saveDetail.header.delete')}
                                        title={t('saveDetail.header.delete')}
                                        onClick={() => deleteConfigurationHandler(item.id)}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className={styles.SaveComponent__wrapper__newSave}>
                        {/* <form onSubmit={createNewSave()}>
                            <input type="text" id="newSaveName" placeholder={t('saveDetail.form.newSaveName')}/>
                        </form> */}
                        <div style={{ minWidth: '50rem'}}>
                            <Form heading={t('saveDetail.form.heading')} onSubmit={handleSubmit}>
                                <Form.FormInput
                                    type="text"
                                    icon="/assets/icons/label.svg"
                                    onChange={(e) => setNewSaveName(e.target.value)}
                                    label={t('saveDetail.form.newSaveLabel')}
                                    placeholder={t('saveDetail.form.newSavePlaceholder')} 
                                />
                                <Button width="100%" margin="1rem 0 0 0" variant="fill" size="lg" type="submit" disabled={checkSubmit()}>
                                    {t('save')}
                                </Button>
                            </Form>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default SaveComponent;